import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Info, Mail, ShieldCheck, Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLoading } from "@/lib/loading";
import { useToast } from "@/lib/toast";
import { maskE164 } from "@/lib/phone";
import { Button, Card, Input } from "@/components/app/Primitives";
import { PhoneInput } from "@/components/app/PhoneInput";

function mask(str) {
  if (!str) return "";
  if (str.includes("@")) {
    const [local, domain] = str.split("@");
    if (!local || !domain) return str;
    return `${local.slice(0, 2)}${"*".repeat(Math.max(1, local.length - 2))}@${domain}`;
  }
  return maskE164(str) || (str.length > 4 ? `••••${str.slice(-4)}` : str);
}

function StatusRow({ icon: Icon, tone, title, sub, right, rightTone }) {
  const bg = tone === "success" ? "bg-a-success/15 text-a-success" : "bg-a-dim text-a-primary";
  return (
    <div className="flex items-center gap-2.5">
      <span className={`flex h-9 w-9 items-center justify-center rounded-[10px] ${bg}`}><Icon size={18} /></span>
      <div className="flex-1">
        <p className="font-extrabold text-a-text">{title}</p>
        <p className="text-xs text-a-muted">{sub}</p>
      </div>
      <span className={`text-xs font-bold ${rightTone}`}>{right}</span>
    </div>
  );
}

function CodeRow({ testID, label, code, setCode, onResend, cooldown, sent, sendLabel, resendTestID }) {
  return (
    <div className="flex items-end gap-2">
      <Input testID={testID} label={label} placeholder="123456" inputMode="numeric" maxLength={6} value={code} onChangeText={(t) => setCode(t.replace(/[^0-9]/g, ""))} />
      <button onClick={onResend} disabled={cooldown > 0} data-testid={resendTestID}
        className="h-12 shrink-0 whitespace-nowrap rounded-xl border border-a-border px-3.5 text-xs font-bold text-a-primary hover:bg-a-dim disabled:opacity-50">
        {cooldown > 0 ? `Resend in ${cooldown}s` : sent ? "Resend" : sendLabel}
      </button>
    </div>
  );
}

export default function Verify() {
  const { user, refreshMe, signOut, activeWorkspace } = useAuth();
  const toast = useToast();
  const { runWithLoading } = useLoading();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mobileFlow = params.get("target") === "mobile";
  const [emailCode, setEmailCode] = useState("");
  const [mobileCode, setMobileCode] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [mobileBusy, setMobileBusy] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);
  const [mobileCooldown, setMobileCooldown] = useState(0);
  const [sentEmailOnce, setSentEmailOnce] = useState(false);
  const [sentMobileOnce, setSentMobileOnce] = useState(false);
  const [editingMobile, setEditingMobile] = useState(false);
  const [newMobile, setNewMobile] = useState("");
  const [changingMobile, setChangingMobile] = useState(false);

  const hasMobile = !!user?.mobile_number;
  const emailDone = !!user?.email_verified;
  const mobileExempt = !!user?.mobile_verification_exempt;
  const mobileDone = mobileExempt || (hasMobile && !!user?.mobile_verified);
  const allDone = emailDone && mobileDone;

  useEffect(() => {
    if (!user || !allDone) return;
    if (mobileFlow) navigate("/profile/edit", { replace: true });
    else navigate(activeWorkspace ? "/" : "/workspaces", { replace: true });
  }, [allDone, user, navigate, mobileFlow, activeWorkspace]);

  useEffect(() => {
    if (emailCooldown <= 0 && mobileCooldown <= 0) return undefined;
    const t = setInterval(() => { setEmailCooldown((v) => (v > 0 ? v - 1 : 0)); setMobileCooldown((v) => (v > 0 ? v - 1 : 0)); }, 1000);
    return () => clearInterval(t);
  }, [emailCooldown, mobileCooldown]);

  const emailMasked = useMemo(() => mask(user?.email), [user?.email]);
  const mobileMasked = useMemo(() => mask(user?.mobile_number), [user?.mobile_number]);

  const resendEmail = async () => {
    if (emailCooldown > 0) return;
    try {
      const r = await runWithLoading(() => api("/auth/resend-email-verification", { method: "POST" }), "Sending email code…");
      setSentEmailOnce(true); setEmailCooldown(45);
      if (r?.email_sent === false) toast.show("Couldn't send email right now. Please try again in a minute or contact support.", "error");
      else toast.show(`Verification code sent to ${emailMasked}`, "info");
    } catch (e) { toast.show(e instanceof Error ? e.message : "Could not send code", "error"); }
  };
  const submitEmail = async () => {
    const code = emailCode.trim();
    if (code.length < 4) return toast.show("Enter the 6-digit code", "error");
    setEmailBusy(true);
    try {
      await runWithLoading(() => api("/auth/verify-email-otp", { method: "POST", body: { code } }), "Verifying email…");
      toast.show("Email verified", "success"); setEmailCode(""); await refreshMe();
    } catch (e) { toast.show(e instanceof Error ? e.message : "Verification failed", "error"); } finally { setEmailBusy(false); }
  };
  const resendMobile = async () => {
    if (mobileCooldown > 0) return;
    try {
      const r = await runWithLoading(() => api("/auth/send-mobile-otp", { method: "POST", body: {} }), "Sending OTP…");
      setSentMobileOnce(true); setMobileCooldown(45);
      if (r?.sms_sent === false) toast.show(r.sms_error || "SMS OTP could not be delivered. Please double-check the number or try again shortly.", "error");
      else toast.show(`OTP sent to ${mobileMasked}`, "info");
    } catch (e) { toast.show(e instanceof Error ? e.message : "Could not send OTP", "error"); }
  };
  const submitMobile = async () => {
    const code = mobileCode.trim();
    if (code.length < 4) return toast.show("Enter the 6-digit OTP", "error");
    setMobileBusy(true);
    try {
      await runWithLoading(() => api("/auth/verify-mobile-otp", { method: "POST", body: { code } }), "Verifying mobile…");
      toast.show("Mobile verified", "success"); setMobileCode(""); await refreshMe();
    } catch (e) { toast.show(e instanceof Error ? e.message : "Verification failed", "error"); } finally { setMobileBusy(false); }
  };
  const saveNewMobile = async () => {
    if (!newMobile) return toast.show("Enter a valid mobile number", "error");
    setChangingMobile(true);
    try {
      await api("/auth/change-mobile", { method: "POST", body: { mobile_number: newMobile } });
      toast.show("OTP sent to your new mobile", "success");
      setEditingMobile(false); setSentMobileOnce(true); setMobileCooldown(45); setNewMobile("");
      await refreshMe();
    } catch (e) { toast.show(e instanceof Error ? e.message : "Failed to update mobile", "error"); } finally { setChangingMobile(false); }
  };

  if (!user) return null;
  return (
    <div className="min-h-screen bg-a-bg">
      <header className="sticky top-0 z-40 border-b border-a-border bg-a-bg/90 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center gap-2.5 px-4 py-3">
          <div className="flex-1">
            <h1 className="text-[17px] font-extrabold text-a-text">Verify your account</h1>
            <p className="text-xs text-a-muted">Both email and mobile verification are required to unlock Assignoo.</p>
          </div>
          <button onClick={async () => { await signOut(); navigate("/login", { replace: true }); }} className="font-bold text-a-muted hover:text-a-text" data-testid="verify-signout">Sign out</button>
        </div>
      </header>
      <div className="page-in mx-auto max-w-2xl space-y-3.5 p-4">
        <Card>
          <StatusRow icon={emailDone ? CheckCircle2 : Mail} tone={emailDone ? "success" : ""} title="Email" sub={emailMasked || "not set"}
            right={emailDone ? "Verified" : "Pending"} rightTone={emailDone ? "text-a-success" : "text-a-muted"} />
          <div className="mt-3">
            {!mobileDone ? (
              <StatusRow icon={Smartphone} title="Mobile" sub={mobileMasked || "add a mobile number"} right="Pending" rightTone="text-a-muted" />
            ) : mobileExempt && !user.mobile_verified ? (
              <StatusRow icon={ShieldCheck} title="Mobile" sub="Not required for this account" right="Exempt" rightTone="text-a-primary" />
            ) : (
              <StatusRow icon={CheckCircle2} tone="success" title="Mobile" sub={mobileMasked} right="Verified" rightTone="text-a-success" />
            )}
          </div>
        </Card>

        {!emailDone ? (
          <Card testID="verify-email-card">
            <p className="text-[15px] font-extrabold text-a-text">Verify email</p>
            <p className="mb-3 mt-1 text-[13px] text-a-muted">We&apos;ll send a 6-digit code to <b className="text-a-text">{emailMasked}</b>.</p>
            <CodeRow testID="verify-email-code" resendTestID="verify-email-send" label="Code" code={emailCode} setCode={setEmailCode} onResend={resendEmail} cooldown={emailCooldown} sent={sentEmailOnce} sendLabel="Send code" />
            <div className="mt-3"><Button testID="verify-email-submit" label="Verify email" onPress={submitEmail} loading={emailBusy} /></div>
          </Card>
        ) : null}

        {!mobileDone ? (
          <Card testID="verify-mobile-card">
            <div className="flex items-center justify-between">
              <div className="flex flex-1 items-center gap-2">
                <p className="text-[15px] font-extrabold text-a-text">Verify mobile</p>
                <span className="rounded-md bg-a-danger/15 px-2 py-0.5 text-[10px] font-extrabold tracking-[0.5px] text-a-danger">REQUIRED</span>
              </div>
              {hasMobile ? (
                <button onClick={() => setEditingMobile((v) => !v)} className="text-xs font-bold text-a-primary" data-testid="verify-change-number">{editingMobile ? "Cancel" : "Change number"}</button>
              ) : null}
            </div>
            <p className="mb-3 mt-1 text-[13px] text-a-muted">
              {hasMobile ? <>We&apos;ll SMS an OTP to <b className="text-a-text">{mobileMasked}</b>. Mobile verification is required to continue.</> : <>Add your mobile number to receive an OTP. This is required to continue.</>}
            </p>
            {editingMobile || !hasMobile ? (
              <div className="space-y-2.5">
                <PhoneInput testID="verify-new-mobile" label={hasMobile ? "New mobile number" : "Mobile number"} placeholder="98765 43210" valueE164={newMobile} onChangeE164={(e) => setNewMobile(e)} />
                <Button testID="verify-save-mobile" label="Save & send OTP" onPress={saveNewMobile} loading={changingMobile} />
              </div>
            ) : (
              <>
                <CodeRow testID="verify-mobile-code" resendTestID="verify-mobile-send" label="OTP" code={mobileCode} setCode={setMobileCode} onResend={resendMobile} cooldown={mobileCooldown} sent={sentMobileOnce} sendLabel="Send OTP" />
                <div className="mt-3"><Button testID="verify-mobile-submit" label="Verify mobile" onPress={submitMobile} loading={mobileBusy} /></div>
              </>
            )}
          </Card>
        ) : null}

        <Card>
          <div className="flex items-start gap-2">
            <Info size={18} className="shrink-0 text-a-muted" />
            <div className="flex-1">
              <p className="text-xs leading-[18px] text-a-muted">Didn&apos;t get a code? Check spam / SMS inbox, then tap Resend. Codes expire in 10–15 minutes.</p>
              <p className="mt-1.5 text-[11px] leading-[17px] text-a-muted">Note: If you have promotional-SMS DND enabled on your carrier, transactional OTPs may be delayed up to a few minutes. If you still don&apos;t receive it, try Change number, or reach out via Profile → Support.</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
